import { db } from '../firebase';
import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { StoreLocation } from '../types';
import { getStaff } from './staffService';
import { getSettings } from './settingsService';
import { getFormattedTimestamp } from '../lib/utils';

const DEFAULT_LOCATIONS: StoreLocation[] = [
  {
    id: 'loc-downtown',
    name: 'Downtown Bistro',
    code: 'LOC-DT01',
    address: '1200 4th Ave',
    city: 'Seattle',
    state: 'WA',
    zip: '98101',
    region: 'North Region',
    timezone: 'America/Los_Angeles',
    phone: '(206) 555-0142',
    status: 'active',
    isPrimary: true,
  },
  {
    id: 'loc-uptown',
    name: 'Uptown Taphouse',
    code: 'LOC-UP02',
    address: '500 Bellevue Way NE',
    city: 'Bellevue',
    state: 'WA',
    zip: '98004',
    region: 'North Region',
    timezone: 'America/Los_Angeles',
    phone: '(425) 555-0189',
    status: 'active',
    isPrimary: false,
  },
  {
    id: 'loc-airport',
    name: 'Airport Terminal 2',
    code: 'LOC-SEA03',
    address: '17801 International Blvd',
    city: 'SeaTac',
    state: 'WA',
    zip: '98158',
    region: 'South Region',
    timezone: 'America/Los_Angeles',
    phone: '(206) 555-0211',
    status: 'active',
    isPrimary: false,
  },
  {
    id: 'loc-westside',
    name: 'Westside Cafe',
    code: 'LOC-TAC04',
    address: '901 Pacific Ave',
    city: 'Tacoma',
    state: 'WA',
    zip: '98402',
    region: 'West Region',
    timezone: 'America/Los_Angeles',
    phone: '(253) 555-0377',
    status: 'active',
    isPrimary: false,
  },
];

/**
 * Loads store locations for a given tenant, computing the live count of assigned staff.
 * If no locations exist in Firestore yet, automatically seeds default locations based on tenant settings.
 */
export const getStoreLocations = async (tenantId: string): Promise<StoreLocation[]> => {
  if (!tenantId) return [];

  const locationsRef = collection(db, 'tenants', tenantId, 'locations');

  try {
    const [snapshot, staffList, settings] = await Promise.all([
      getDocs(locationsRef),
      getStaff(tenantId),
      getSettings(tenantId).catch(() => null),
    ]);

    let locations: StoreLocation[] = [];

    if (snapshot.empty) {
      // Seed default locations
      const batch = writeBatch(db);
      const defaultTz = settings?.timezone || 'America/Los_Angeles';

      locations = DEFAULT_LOCATIONS.map((loc, idx) => {
        const item: StoreLocation = {
          ...loc,
          timezone: defaultTz,
          isPrimary: idx === 0,
        };
        const docRef = doc(locationsRef, item.id);
        batch.set(docRef, item);
        return item;
      });

      await batch.commit();
    } else {
      locations = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as StoreLocation));
    }

    // Calculate staff count per location
    const staffCountMap: Record<string, number> = {};
    staffList.forEach(s => {
      const primaryLoc = s.locationId || 'loc-downtown';
      staffCountMap[primaryLoc] = (staffCountMap[primaryLoc] || 0) + 1;

      if (s.assignedLocationIds && Array.isArray(s.assignedLocationIds)) {
        s.assignedLocationIds.forEach(locId => {
          if (locId !== primaryLoc) {
            staffCountMap[locId] = (staffCountMap[locId] || 0) + 1;
          }
        });
      }
    });

    return locations.map(loc => ({
      ...loc,
      staffCount: staffCountMap[loc.id] || 0,
    }));
  } catch (error) {
    console.error('Error fetching store locations:', error);
    return DEFAULT_LOCATIONS;
  }
};

/**
 * Saves (creates or updates) a store location.
 */
export const saveStoreLocation = async (
  tenantId: string,
  location: Partial<StoreLocation> & { name: string }
): Promise<string> => {
  const locationsRef = collection(db, 'tenants', tenantId, 'locations');
  const locationId = location.id || `loc-${location.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now().toString().slice(-4)}`;
  const locationDoc = doc(locationsRef, locationId);

  const payload: StoreLocation = {
    id: locationId,
    name: location.name,
    code: location.code || `LOC-${location.name.substring(0, 3).toUpperCase()}`,
    address: location.address || '',
    city: location.city || '',
    state: location.state || '',
    zip: location.zip || '',
    region: location.region || 'North Region',
    timezone: location.timezone || 'America/Los_Angeles',
    phone: location.phone || '',
    status: location.status || 'active',
    isPrimary: !!location.isPrimary,
    updatedAt: new Date().toISOString(),
  };

  await setDoc(locationDoc, payload, { merge: true });
  return locationId;
};

/**
 * Sets a specific location as the primary store.
 */
export const setPrimaryLocation = async (tenantId: string, locationId: string): Promise<void> => {
  const locationsRef = collection(db, 'tenants', tenantId, 'locations');
  const snapshot = await getDocs(locationsRef);
  const batch = writeBatch(db);

  snapshot.docs.forEach(d => {
    batch.update(doc(locationsRef, d.id), {
      isPrimary: d.id === locationId,
      updatedAt: new Date().toISOString(),
    });
  });

  await batch.commit();
};

/**
 * Toggles a location's active/inactive status.
 */
export const toggleLocationStatus = async (
  tenantId: string,
  locationId: string,
  currentStatus: 'active' | 'inactive'
): Promise<void> => {
  const locationRef = doc(db, 'tenants', tenantId, 'locations', locationId);
  const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
  await updateDoc(locationRef, {
    status: newStatus,
    updatedAt: new Date().toISOString(),
  });
};

/**
 * Deletes a store location from Firestore.
 */
export const deleteStoreLocation = async (tenantId: string, locationId: string): Promise<void> => {
  const locationRef = doc(db, 'tenants', tenantId, 'locations', locationId);
  await deleteDoc(locationRef);
};

export interface HistoricalBackfillStatus {
  totalScanned: number;
  unmigratedCount: number;
  migratedCount: number;
  byCollection: {
    transactions: number;
    shifts: number;
    cashTips: number;
    disputes: number;
    staff: number;
  };
  isFullyAligned: boolean;
  primaryLocation: StoreLocation | null;
}

/**
 * Scans tenant collections for legacy documents created before multi-location tagging.
 */
export const checkHistoricalBackfillStatus = async (tenantId: string): Promise<HistoricalBackfillStatus> => {
  if (!tenantId) {
    return {
      totalScanned: 0,
      unmigratedCount: 0,
      migratedCount: 0,
      byCollection: { transactions: 0, shifts: 0, cashTips: 0, disputes: 0, staff: 0 },
      isFullyAligned: true,
      primaryLocation: null,
    };
  }

  const locations = await getStoreLocations(tenantId);
  const primaryLoc = locations.find(l => l.isPrimary) || locations[0] || null;

  const scanTargets: Array<{
    colName: string;
    category: 'transactions' | 'shifts' | 'cashTips' | 'disputes' | 'staff';
  }> = [
    { colName: 'master_transactions', category: 'transactions' },
    { colName: 'cash_tips', category: 'cashTips' },
    { colName: 'jar_tips', category: 'cashTips' },
    { colName: 'shifts', category: 'shifts' },
    { colName: 'disputes', category: 'disputes' },
    { colName: 'staff', category: 'staff' },
    { colName: 'transactions', category: 'transactions' },
    { colName: 'cashTips', category: 'cashTips' },
    { colName: 'jarTips', category: 'cashTips' },
  ];

  const byCollection = {
    transactions: 0,
    shifts: 0,
    cashTips: 0,
    disputes: 0,
    staff: 0,
  };

  let totalScanned = 0;
  let unmigratedCount = 0;

  await Promise.all(
    scanTargets.map(async target => {
      try {
        const colRef = collection(db, 'tenants', tenantId, target.colName);
        const snapshot = await getDocs(colRef);
        totalScanned += snapshot.size;

        snapshot.docs.forEach(d => {
          const data = d.data();
          const hasLocation = !!data.locationId && data.locationId !== 'all';
          if (!hasLocation) {
            unmigratedCount++;
            byCollection[target.category]++;
          }
        });
      } catch (err) {
        console.warn(`[checkHistoricalBackfillStatus] Info scanning collection ${target.colName}:`, err);
      }
    })
  );

  return {
    totalScanned,
    unmigratedCount,
    migratedCount: Math.max(0, totalScanned - unmigratedCount),
    byCollection,
    isFullyAligned: unmigratedCount === 0,
    primaryLocation: primaryLoc,
  };
};

/**
 * Performs a zero-downtime historical data backfill.
 * Updates legacy documents in batches of up to 400 docs with the specified location.
 */
export const runHistoricalDataBackfill = async (
  tenantId: string,
  targetLocationId: string,
  targetLocationName?: string
): Promise<{ success: boolean; updatedCount: number; message: string }> => {
  if (!tenantId || !targetLocationId) {
    throw new Error('Tenant ID and Target Location ID are required for backfill.');
  }

  const settings = await getSettings(tenantId).catch(() => null);
  const timestamp = getFormattedTimestamp(settings?.timezone || 'UTC');

  const collectionsToUpdate = [
    'master_transactions',
    'cash_tips',
    'jar_tips',
    'shifts',
    'disputes',
    'staff',
    'transactions',
    'cashTips',
    'jarTips',
  ];
  let totalUpdated = 0;

  for (const colName of collectionsToUpdate) {
    try {
      const colRef = collection(db, 'tenants', tenantId, colName);
      const snapshot = await getDocs(colRef);

      const docsToUpdate = snapshot.docs.filter(d => {
        const data = d.data();
        return !data.locationId || data.locationId === 'all';
      });

      if (docsToUpdate.length === 0) continue;

      // Batch in chunks of 400
      const CHUNK_SIZE = 400;
      for (let i = 0; i < docsToUpdate.length; i += CHUNK_SIZE) {
        const chunk = docsToUpdate.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);

        chunk.forEach(d => {
          batch.update(doc(colRef, d.id), {
            locationId: targetLocationId,
            locationName: targetLocationName || 'Downtown Bistro',
            historicalMigrationTimestamp: timestamp,
            updatedAt: new Date().toISOString(),
          });
        });

        await batch.commit();
        totalUpdated += chunk.length;
      }
    } catch (err) {
      console.warn(`[runHistoricalDataBackfill] Info backfilling ${colName}:`, err);
    }
  }

  return {
    success: true,
    updatedCount: totalUpdated,
    message: `Historical backfill completed. Successfully mapped ${totalUpdated} legacy record(s) to ${targetLocationName || targetLocationId}.`,
  };
};

